import { Box, IconButton, makeStyles, Menu, Slider } from '@material-ui/core';
import { useState } from 'react';

import { TermValueField } from '@/components/modules/Application/components/PaymentMethod/_shared/components/LoanTermSelector/components/TermValueTextField';
import { TermSelectorSchema } from '@/components/modules/Application/components/PaymentMethod/_shared/components/schemas/TermSelector.schema';
import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { Font } from '@/components/shared/components/Font';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { isArrayContinuous } from '@/utils/isArrayContinuous';

interface LoanTermSelectorProps<T extends AbstractLoanPaymentMethod> {
  paymentMethodEntity: T;
}

export const LoanTermSelector = <T extends AbstractLoanPaymentMethod>({
  paymentMethodEntity,
}: LoanTermSelectorProps<T>) => {
  const { updatePaymentMethodEntity } = usePaymentMethods();
  const { palette } = useCustomTheme();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const selectedTerm = paymentMethodEntity.selectedTerm;

  if (!selectedTerm) {
    return null;
  }

  const areTermsValuesContinuous = isArrayContinuous(paymentMethodEntity.allTerms);

  const currentIndex = paymentMethodEntity.proposedTerms.indexOf(selectedTerm);

  const useStyles = makeStyles({
    root: {
      '&.MuiSlider-colorSecondary': { color: palette.grey[200] },
      '& .MuiSlider-rail': { opacity: 1 },
      '& .MuiSlider-thumb': { color: palette.secondary.main },
      '& .MuiSlider-markActive': {
        backgroundColor: palette.secondary.main,
        opacity: 1,
      },
      '& .MuiSlider-mark': {
        backgroundColor: palette.secondary.main,
        borderRadius: 0,
        width: `${100 / paymentMethodEntity.availableTerms.length + 0.1}%`,
      },
    },
  });

  const classes = useStyles();

  let prevTerm: number | undefined = paymentMethodEntity.proposedTerms[currentIndex - 1];
  let nextTerm: number | undefined = paymentMethodEntity.proposedTerms[currentIndex + 1];
  if (currentIndex === -1) {
    prevTerm = paymentMethodEntity.proposedTerms.reverse().find((term) => selectedTerm > term);
    nextTerm = paymentMethodEntity.proposedTerms.find((term) => selectedTerm < term);
  }

  return (
    <Box display={'flex'} gridGap={4} alignItems={'center'}>
      <Box display={'flex'} alignItems={'center'}>
        <IconButton
          size="small"
          disabled={!prevTerm}
          onClick={() => {
            if (prevTerm) {
              const newPaymentMethodEntity = paymentMethodEntity.updateSelectedTerm(prevTerm);
              updatePaymentMethodEntity(newPaymentMethodEntity);
            }
          }}
        >
          <Icon size={20}>chevron_left</Icon>
        </IconButton>
        <Font variant="body1">
          <strong>{selectedTerm}</strong>
        </Font>
        <IconButton
          size="small"
          disabled={!nextTerm}
          onClick={() => {
            if (nextTerm) {
              const newPaymentMethodEntity = paymentMethodEntity.updateSelectedTerm(nextTerm);
              updatePaymentMethodEntity(newPaymentMethodEntity);
            }
          }}
        >
          <Icon size={20}>chevron_right</Icon>
        </IconButton>
        <Font variant="body1">
          <strong>
            {' '}
            <Text tId={'MONTHS'} />
          </strong>
        </Font>
      </Box>
      {areTermsValuesContinuous && (
        <>
          <IconButton onClick={handleClick} style={{ marginTop: '2px' }}>
            <Icon size={18}>tune</Icon>
          </IconButton>
          <Menu
            getContentAnchorEl={null}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={open}
            keepMounted
            onClose={handleClose}
          >
            <Box
              display={'flex'}
              flexDirection={'column'}
              gridGap={2}
              alignItems={'flex-start'}
              minWidth={'240px'}
              maxWidth={'300px'}
              px={2}
            >
              <Font color="textPrimary" variant="caption">
                <Text tId={'TERM'} />
              </Font>
              <FormWrapper formSchema={TermSelectorSchema} defaultValues={{ term: selectedTerm.toString() }}>
                <TermValueField paymentMethodEntity={paymentMethodEntity} handleClose={handleClose} />
              </FormWrapper>
              <Slider
                className={classes.root}
                color="secondary"
                min={paymentMethodEntity.allTerms[0]}
                max={paymentMethodEntity.allTerms[paymentMethodEntity.allTerms.length - 1]}
                value={selectedTerm as number}
                marks={paymentMethodEntity.availableTerms.map((term) => ({ value: term }))}
                onChangeCommitted={(_, newValue) => {
                  updatePaymentMethodEntity(paymentMethodEntity.updateSelectedTerm(newValue as number));
                }}
              />
            </Box>
          </Menu>
        </>
      )}
    </Box>
  );
};
